import { createSelector } from '@ngxs/store';
import { denormalize } from 'normalizr';
import { produce } from 'immer';
import _ from 'lodash';

import {
  Diner,
  dinersSchema,
  MealPlanType,
  MenuItemType,
  NormalizedEntities,
  Seat,
  seatsSchema,
  Table,
  tableSchema,
  Ticket,
  TicketItemStatus,
  ticketsSchema,
  TicketStatus
} from 'src/app/models';
import { TicketHelper } from 'src/app/helpers/ticket.helper';
import { TableHelper } from 'src/app/helpers';
import { SeatsStateSelectors } from 'src/app/store/seats/seats.state.selectors';
import { TicketItemsStateSelectors } from 'src/app/store/ticket-items/ticket-items-state.selectors';
import {
  TABLES_STATE_TOKEN,
  TableStateModel
} from 'src/app/store/tables/tables.state.model';
import { TICKETS_STATE_TOKEN } from './tickets/tickets.state';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';
import { MenuSelectors } from 'src/app/store/menu/menu.selector';
import { TransactionsSelectors } from 'src/app/store/transactions/transactions.selectors';
import { LocationSelectors } from 'src/app/store/location/location.selectors';
import { DinersSelectors } from 'src/app/store/diners/diners.selectors';
import { PosTenantType } from 'src/app/constants';

export interface MealPlanTicketInfo {
  mealplan_tender_type: MealPlanType;
  meal_plan_id: number;
}

export class UtilsSelectors {
  static selectedTicket = createSelector(
    [
      TicketsStateSelectors.selectedTicket,
      TicketsStateSelectors.tickets,
      TicketItemsStateSelectors.ticketItemsById,
      TransactionsSelectors.transactionsById,
      DinersSelectors.dinersById,
      TicketItemsStateSelectors.getWaitingTicketItem
    ],
    (
      selectedTicketData,
      { byId: ticketsByUuid },
      normalizedTicketItems,
      transactionsById,
      normalizedDiners,
      waitingTicketItem
    ) => {
      const selectedTicketId = selectedTicketData.data;

      if (!selectedTicketId) {
        return null;
      }

      const denormalizeSelectedTicket: Ticket = denormalize(
        selectedTicketId,
        ticketsSchema,
        {
          tickets: ticketsByUuid,
          ticket_items: normalizedTicketItems,
          transactions: transactionsById
        }
      );

      if (!denormalizeSelectedTicket) {
        return;
      }

      denormalizeSelectedTicket.ticket_items = _.compact(
        denormalizeSelectedTicket.ticket_items
      );

      const feeItems = denormalizeSelectedTicket.ticket_items.filter(
        (ti) => ti.is_fee_item
      );
      denormalizeSelectedTicket.ticket_items =
        denormalizeSelectedTicket.ticket_items.filter((ti) => !ti.is_fee_item);

      if (feeItems?.length) {
        denormalizeSelectedTicket.local_attributes = {
          ...denormalizeSelectedTicket.local_attributes,
          ticketItemFees: feeItems
        };
      }

      denormalizeSelectedTicket.local_attributes = {
        ...denormalizeSelectedTicket.local_attributes,
        subtotal: TicketHelper.calculateTicketSubtotal(
          denormalizeSelectedTicket
        ),
        discountApplied: TicketHelper.calculateTicketDiscountApplied(
          denormalizeSelectedTicket
        )
      };

      const { diner_id, guest_of_diner_id } = denormalizeSelectedTicket;
      const ticketDinerId = diner_id || guest_of_diner_id;

      if (ticketDinerId) {
        denormalizeSelectedTicket.diner = denormalize(
          ticketDinerId,
          dinersSchema,
          {
            diners: normalizedDiners
          }
        ) as Diner;
      }

      denormalizeSelectedTicket.ticket_items = _.chain(
        denormalizeSelectedTicket.ticket_items
      )
        .filter((i) => i && !i.parent_uuid)
        .sortBy('created_at')
        .value();

      if (
        waitingTicketItem?.length &&
        _.first(waitingTicketItem).device_ticket_uuid ===
          denormalizeSelectedTicket.device_ticket_uuid
      ) {
        // applied waiting item for normal items
        denormalizeSelectedTicket.ticket_items =
          denormalizeSelectedTicket.ticket_items.concat(
            (waitingTicketItem as any[]).filter((x) => !x.parent_uuid)
          );
        const waitingChildren = (waitingTicketItem as any[]).filter(
          (x) => x.parent_uuid
        );
        if (waitingChildren.length) {
          denormalizeSelectedTicket.ticket_items =
            denormalizeSelectedTicket.ticket_items.map((item) => {
              const childrenToAdd = waitingChildren.filter(
                (x) => x.parent_uuid === item.device_ticket_item_uuid
              );
              if (!childrenToAdd.length) {
                return item;
              }
              return {
                ...item,
                ticket_items: [...(item.ticket_items || []), ...childrenToAdd]
              };
            });
        }
      }

      denormalizeSelectedTicket.local_attributes = {
        ...denormalizeSelectedTicket.local_attributes,
        isItemsFiredOrVoided:
          denormalizeSelectedTicket.ticket_items &&
          denormalizeSelectedTicket.ticket_items.every(
            (x) =>
              x?.status === TicketItemStatus.FIRED ||
              x?.status === TicketItemStatus.VOIDED
          )
      };

      if (_.isString(denormalizeSelectedTicket.ticket_date)) {
        denormalizeSelectedTicket.formatted_takeout_date_time =
          TicketHelper.calculateFormattedTakeoutDateTime(
            denormalizeSelectedTicket
          );
      }

      return denormalizeSelectedTicket;
    }
  );

  static canDiscount = createSelector(
    [
      TICKETS_STATE_TOKEN,
      TicketItemsStateSelectors.getSelectedTicketItem,
      TicketItemsStateSelectors.ticketItemsById,
      MenuSelectors.getMenuItems
    ],
    (ticketsState, selectedTicketItem, ticketItems, menuItems) => {
      const selectedTicketId = ticketsState.selectedTicket.data;
      const ticketsById = ticketsState.tickets.byId;
      const selectedTicket =
        !ticketsById || !selectedTicketId
          ? null
          : ticketsById[selectedTicketId];
      const isTicketPaid = [
        TicketStatus.PARTIAL_PAID,
        TicketStatus.PAID
      ].includes(selectedTicket.status);
      const isTicketItemVoided =
        selectedTicketItem?.status === TicketItemStatus.VOIDED;
      let canBeDiscounted = true;

      // if ticket is paid or ticket item is voided, it can't be discounted
      if (isTicketPaid || isTicketItemVoided) {
        canBeDiscounted = false;
      } else if (selectedTicketItem) {
        // if ticket item is selected
        const menuItem = menuItems.find(({ type, food_id, id }) => {
          const isFood = type === MenuItemType.Food;
          return isFood
            ? food_id === selectedTicketItem.food_id
            : id === selectedTicketItem.pos_product_id;
        });

        canBeDiscounted = menuItem?.can_be_discounted; // enable discount if points plan has been used
      }

      return canBeDiscounted;
    }
  );

  private static _tablesNormalized = createSelector(
    [TABLES_STATE_TOKEN],
    (state: TableStateModel) => state.tables
  );

  private static _selectedTableId = createSelector(
    [TABLES_STATE_TOKEN],
    (state: TableStateModel) => state.selectedTable.data
  );

  static getSelectedTable = createSelector(
    [
      UtilsSelectors._tablesNormalized,
      UtilsSelectors._selectedTableId,
      SeatsStateSelectors.seatsById,
      TicketsStateSelectors.ticketsById,
      TicketItemsStateSelectors.ticketItemsById,
      DinersSelectors.dinersById,
      TransactionsSelectors.transactionsById
    ],
    (
      tables,
      selectedTableId,
      seats,
      ticketsById,
      ticketItemsById,
      dinersById,
      transactionsById
    ) => {
      if (selectedTableId == null) {
        return null;
      }

      const entities: NormalizedEntities = {
        tables: tables.byId,
        seats,
        tickets: ticketsById,
        ticket_items: ticketItemsById,
        diners: dinersById,
        transactions: transactionsById
      };

      const denormalizedTable: Table = denormalize(
        selectedTableId,
        tableSchema,
        entities
      );

      if (denormalizedTable && denormalizedTable.seats) {
        denormalizedTable.seats = _.sortBy(denormalizedTable.seats, 'number');
        denormalizedTable.seats.forEach((s) => {
          if (s && s.ticket && s.ticket.ticket_items) {
            // This filter was not here before but had to add it to prevent app from displaying modifiers as regular items
            // Please investigate root cause for this happening
            // DEF-3210 - When cancelling any item from ticket screen , at checkout modifiers are getting duplicated.
            s.ticket.ticket_items = s.ticket.ticket_items.filter(
              (ti) => !ti.parent_uuid
            );
            s.ticket.ticket_items = _.sortBy(
              s.ticket.ticket_items,
              'created_at'
            );
          }
        });
      }

      return denormalizedTable;
    }
  );

  static getSelectedCheckoutTable = createSelector(
    [UtilsSelectors.getSelectedTable],
    (table) =>
      produce(_.cloneDeep(table), (draft: Table) => {
        draft.seats
          .filter((s) => !!s.ticket?.ticket_items?.length)
          .forEach(({ ticket }) => {
            const ticketItems = ticket?.ticket_items || [];
            _.remove(ticketItems, (ti) => !!ti.parent_uuid);
          });
      })
  );

  static getSeatsCourseStatuses = createSelector(
    [
      UtilsSelectors.getSelectedTable,
      LocationSelectors.getIsCoursingEnabled,
      TicketsStateSelectors.getTicketBySeatId,
      TicketItemsStateSelectors.getTicketItemByUuid
    ],
    (
      selectedTable,
      isCoursingEnabled,
      getTicketBySeatId,
      getTicketItemByUuid
    ) => {
      if (selectedTable?.seats?.length) {
        return TableHelper.seatsStatues(
          selectedTable.seats,
          isCoursingEnabled,
          getTicketBySeatId,
          getTicketItemByUuid
        );
      }
      return [];
    }
  );

  static getSelectedSeat = createSelector(
    [
      TICKETS_STATE_TOKEN,
      SeatsStateSelectors.getSelectedSeatId,
      SeatsStateSelectors.seatsById
    ],
    (ticketsState, selectedSeat, seats) => {
      if (!selectedSeat) {
        return null;
      }
      const entities = {
        seats,
        tickets: ticketsState.tickets.byId
      };

      return denormalize(selectedSeat, seatsSchema, entities) as Seat;
    }
  );

  static getLogoUrlFromTenant(tenant: PosTenantType): string {
    return tenant !== 'myusuite'
      ? 'assets/image/myusuite/dineos-logo.svg'
      : 'assets/image/app/logo.png';
  }

  static isSelectedTicketHaveToVoid = createSelector(
    [
      SeatsStateSelectors.getSelectedSeatId,
      TicketsStateSelectors.getTicketBySeatId,
      TicketItemsStateSelectors.getTicketItemByUuid
    ],
    (seatId, getTicketBySeatId, getTicketItemByUuid) => {
      const ticket = getTicketBySeatId(seatId);

      if (!ticket) {
        return false;
      }

      return ticket.ticket_items?.some((ticketItemUuid) => {
        const ticketItem = getTicketItemByUuid(ticketItemUuid);

        if (!ticketItem) {
          return false;
        }

        return (
          ticketItem.status === TicketItemStatus.FIRED ||
          ticketItem.status === TicketItemStatus.VOIDED
        );
      });
    }
  );
}
